import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle
} from "react";
import ReactMarkdown from "react-markdown";
import { Buffer } from "buffer";
import axios from "axios";

const DEBUG = false; // Toggle as desired
const DEBUG_FULL_AUDIO_FILES = false; // Toggle as desired
const DEBUG_QUEUE_WAITING = false; // Toggle as desired
const DEBUG_CUSTOM_MUSIC = false; // Toggle as desired

function debugLog(...args) {
  if (DEBUG) {
    console.log("[StreamingMeditation]", ...args);
  }
}

function debugLogFullAudioFiles(...args) {
  if (DEBUG_FULL_AUDIO_FILES) {
    console.log("[StreamingMeditation]", ...args);
  }
}

function debugLogQueueWaiting(...args) {
  if (DEBUG_QUEUE_WAITING) {
    console.log("[StreamingMeditation]", ...args);
  }
}

function debugLogCustomMusic(...args) {
  if (DEBUG_CUSTOM_MUSIC) {
    console.log("[StreamingMeditation]", ...args);
  }
}

function mergeTextWithNewlines(current, newChunk) {
  return current.trim().length > 0 ? current + "\n\n" + newChunk : newChunk;
}

function createPauseAudioUrl(seconds) {
  const sampleRate = 8000;
  const sampleCount = Math.round(seconds * sampleRate);
  const wave = Buffer.alloc(44 + sampleCount, 128);
  wave.write("RIFF", 0);
  wave.writeUInt32LE(36 + sampleCount, 4);
  wave.write("WAVEfmt ", 8);
  wave.writeUInt32LE(16, 16);
  wave.writeUInt16LE(1, 20);
  wave.writeUInt16LE(1, 22);
  wave.writeUInt32LE(sampleRate, 24);
  wave.writeUInt32LE(sampleRate, 28);
  wave.writeUInt16LE(1, 32);
  wave.writeUInt16LE(8, 34);
  wave.write("data", 36);
  wave.writeUInt32LE(sampleCount, 40);
  return `data:audio/wav;base64,${wave.toString("base64")}`;
}

const StreamingMeditation = forwardRef(function StreamingMeditation(
  {
    prompt,
    voice,
    qualityMode,
    onFirstStreamData,
    onStreamInitError,
    onMediaCommand,
    onCustomMusicAvailable,
    onCustomMusicStop,
    onReceivedCompleteText,
    onLastTTSComplete,
    onPlaybackInterrupted,
    onTTSPlay,
    onNewNonPriorityText,
    onVisibleTextChange,
    socket,
    sessionId,
    globalVolume,
    isExistingSession
  },
  ref
) {
  const [fullText, setFullText] = useState("");
  const [visibleText, setVisibleText] = useState("");

  // The main queue of clips (audio/pause/media/finalTTS lines)
  const [audioQueue, setAudioQueue] = useState([]);
  const [sentIdleLine, setSentIdleLine] = useState(false);

  // Have we received finalTTS? (So we can handle pending stopMusic, etc.)
  const [finalTTSReceived, setFinalTTSReceived] = useState(false);

  // Tracks if we are currently in the middle of a pause or an audio playback
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // Tracks if the user explicitly paused. If true, no new audio will start.
  const [manuallyPaused, setManuallyPaused] = useState(false);

  // Holds any stopMusic items until finalTTS is done:
  const pendingStopMusicRef = useRef([]);
  const firstStreamDataSeenRef = useRef(false);
  // Only call /api/stream once
  const hasCalledApiRef = useRef(false);

  // References to current item/timer/audio
  const currentClipRef = useRef(null);
  const currentAudioRef = useRef(null);
  const deliveryRef = useRef(null);
  const receivedPacketsRef = useRef(new Set());
  const lastPacketRef = useRef(0);
  const playbackEndedRef = useRef(false);
  const playbackRunningRef = useRef(false);
  const lastProgressRef = useRef(Date.now());
  const audioPositionRef = useRef(0);
  const disconnectedRef = useRef(false);
  const resumeRequestedRef = useRef(false);
  const lastAcknowledgementRef = useRef(null);
  const interruptionRef = useRef("");
  const lastCompletionRef = useRef(null);
  const playbackHandlersRef = useRef(null);

  function completePlayback(interrupted = false, reason = "complete") {
    if (playbackEndedRef.current) return;
    playbackEndedRef.current = true;
    playbackRunningRef.current = false;
    currentAudioRef.current?.pause();
    currentAudioRef.current = null;
    currentClipRef.current = null;
    setAudioQueue([]);
    setIsAudioPlaying(false);
    setFinalTTSReceived(true);
    const outcome = { deliveryId: deliveryRef.current, packetId: lastPacketRef.current, interrupted, reason };
    lastCompletionRef.current = { sessionId, ...outcome };
    onLastTTSComplete?.(outcome);
    if (interrupted) onPlaybackInterrupted?.(interruptionRef.current || "The meditation stopped after a connection or playback interruption. You can start again.");
    else processPendingStopMusic();
  }

  function requestPlaybackResume() {
    if (!socket?.connected || resumeRequestedRef.current || playbackEndedRef.current || !playbackRunningRef.current) return;
    resumeRequestedRef.current = true;
    socket.emit("resumePlayback", { sessionId, deliveryId: deliveryRef.current, lastPacketId: lastPacketRef.current }, result => {
      resumeRequestedRef.current = false;
      if (!result?.resumed) interruptionRef.current = "The connection could not restore this meditation. Please start again.";
    });
  }
  playbackHandlersRef.current = { completePlayback, requestPlaybackResume };

  useEffect(() => {
    if (!socket) return;
    const disconnected = () => { disconnectedRef.current = true; resumeRequestedRef.current = false; };
    const connected = () => {
      if (!disconnectedRef.current) return;
      disconnectedRef.current = false;
      if (playbackEndedRef.current) {
        if (!socket.recovered && lastCompletionRef.current) socket.emit("stop", lastCompletionRef.current);
        return;
      }
      if (!socket.recovered) playbackHandlersRef.current.requestPlaybackResume();
      if (lastAcknowledgementRef.current) socket.emit("finishedLine", lastAcknowledgementRef.current);
    };
    socket.on("disconnect", disconnected);
    socket.on("connect", connected);
    return () => { socket.off("disconnect", disconnected); socket.off("connect", connected); };
  }, [socket, sessionId]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!playbackRunningRef.current || playbackEndedRef.current) return;
      if (manuallyPaused) { lastProgressRef.current = Date.now(); return; }
      const audio = currentAudioRef.current;
      if (audio && audio.currentTime > audioPositionRef.current) {
        audioPositionRef.current = audio.currentTime;
        lastProgressRef.current = Math.max(lastProgressRef.current, Date.now());
      }
      if (Date.now() - lastProgressRef.current >= 20000) playbackHandlersRef.current.completePlayback(true, "playback_timeout");
    }, 1000);
    return () => clearInterval(timer);
  }, [socket, sessionId, manuallyPaused, onLastTTSComplete, onPlaybackInterrupted]);

  // Keep a ref to the global volume
  const globalVolumeRef = useRef(globalVolume);
  useEffect(() => {
    globalVolumeRef.current = globalVolume;
  }, [globalVolume]);

  useEffect(() => () => {
    currentAudioRef.current?.pause();
    currentAudioRef.current = null;
    currentClipRef.current = null;
  }, []);

  // -----------------------------------------------------------
  // base64 -> data URI
  // -----------------------------------------------------------
  function base64ToDataUrl(base64String) {
    return `data:audio/mp3;base64,${base64String}`;
  }

  // -----------------------------------------------------------
  // processData => merges lines, sets up queue items
  // -----------------------------------------------------------
  function processData(data, isPriority = false) {
    debugLog("processData =>", data);
    if (playbackEndedRef.current) return;
    if (data.error) { interruptionRef.current = data.error; return; }
    if (!isPriority && data.deliveryId) {
      if (deliveryRef.current && deliveryRef.current !== data.deliveryId) return;
      deliveryRef.current = data.deliveryId;
      if (data.packetId !== undefined) {
        if (receivedPacketsRef.current.has(data.packetId)) return;
        if (data.packetId !== lastPacketRef.current + 1) { requestPlaybackResume(); return; }
        receivedPacketsRef.current.add(data.packetId);
        lastPacketRef.current = data.packetId;
      }
    }
    lastProgressRef.current = Math.max(lastProgressRef.current, Date.now());
  
    // If the server signals the entire text
    if (data.meditationText) {
      onReceivedCompleteText?.(data.meditationText);
    }
  
    // Convert base64 => data URI if needed
    if (data.audio && !data.audioUrl) {
      data.audioUrl = base64ToDataUrl(data.audio);
    }
  
    // Gather any relevant info from data
    let finalTTSFlag = false;
    const pauseCommands = [];
    const mediaCommands = [];
  
    // -----------------------------------------------------------
    // 1) Pull out preCommands => pause / media / finalTTS
    // -----------------------------------------------------------
    if (Array.isArray(data.preCommands)) {
      data.preCommands.forEach(cmd => {
        if (cmd.type === "pause") {
          // If you expect multiple (pause) commands in the same chunk,
          // collect them into an array. You can also just keep the
          // last one, depending on your desired behavior.
          pauseCommands.push(cmd.value);
        }
        if (cmd.type === "media") {
          mediaCommands.push(cmd.value);
        }
        if (cmd.type === "finalTTS") {
          finalTTSFlag = true;
        }
      });
    }
  
    // -----------------------------------------------------------
    // 2) Stop music => if we need to queue them after finalTTS
    // -----------------------------------------------------------
    if (data.stopMusic) {
      // If data.audioUrl is still base64, convert it to data URI
      const audioUrl = data.audioUrl
        ? data.audioUrl
        : data.audio
          ? base64ToDataUrl(data.audio)
          : null;
    
      pendingStopMusicRef.current.push({
        stopMusic: true,
        finalTTS: false,
        audioUrl: audioUrl,  // <-- store the actual music URL
      });
      return;
    }
  
    // -----------------------------------------------------------
    // 3) Enqueue pause clips first
    //    - Each pause is a separate clip
    // -----------------------------------------------------------
    pauseCommands.forEach(pValue => {
      if (pValue && typeof pValue === "number") {
        const pauseClip = {
          audioUrl: null,
          text: "",
          writtenText: "",
          pause: pValue,        // # of seconds
          media: null,
          finalTTS: false,      // not marking final yet
          stopMusic: false,
          priority: isPriority
        };
        enqueueClip(pauseClip);
      }
    });
  
    // -----------------------------------------------------------
    // 4) Enqueue all media commands (in sequence)
    //    - Each media is its own clip, with no text/audio/pause
    // -----------------------------------------------------------
    mediaCommands.forEach((mediaPath, idx) => {
      const mediaClip = {
        audioUrl: null,
        text: "",
        writtenText: "",
        pause: null,
        media: mediaPath,
        // If you prefer the last media clip to carry finalTTS (and no text),
        // you could do: finalTTS: (finalTTSFlag && idx === mediaCommands.length - 1)
        // But typically we’ll handle finalTTS on the text/audio clip below.
        finalTTS: false,
        stopMusic: false,
        priority: isPriority
      };
      enqueueClip(mediaClip);
    });
  
    // -----------------------------------------------------------
    // 5) Enqueue any text/audio in one final clip
    //    - This can also hold finalTTS if needed
    // -----------------------------------------------------------
    const hasTextOrAudio = !!(data.spokenText || data.writtenText || data.audioUrl);
    if (hasTextOrAudio) {
      const mainClip = {
        audioUrl: data.audioUrl || null,
        text: data.spokenText || "",
        writtenText: data.writtenText || "",
        pause: null,        // no pause in the text clip
        media: null,        // we already enqueued media above
        finalTTS: false,
        deliveryId: data.deliveryId,
        packetId: data.packetId,
        stopMusic: false,
        priority: isPriority
      };
      enqueueClip(mainClip);
    }
    if (finalTTSFlag) {
      enqueueClip({ finalTTS: true, priority: false });
    }
  
    // If anything was enqueued, reset the idle line
    if (
      pauseCommands.length > 0 ||
      mediaCommands.length > 0 ||
      hasTextOrAudio
    ) {
      setSentIdleLine(false);
    }
  }
  
  // Helper to insert the clip into the queue
  function enqueueClip(clip) {
    if (clip.priority) {
      setAudioQueue(prev => {
        const newQueue = [...prev];
        // Insert right after the last priority item (or at front)
        let lastPriorityIndex = -1;
        for (let i = 0; i < newQueue.length; i++) {
          if (newQueue[i].priority) lastPriorityIndex = i;
        }
        if (lastPriorityIndex === -1) {
          // If no priority items
          if (newQueue.length === 0) newQueue.push(clip);
          else newQueue.splice(1, 0, clip);
        } else {
          newQueue.splice(lastPriorityIndex + 1, 0, clip);
        }
        return newQueue;
      });
    } else {
      // Normal clip => append to end
      setAudioQueue(prev => [...prev, clip]);
    }
  }

  // If the displayed text changes, notify parent
  useEffect(() => {
    onVisibleTextChange?.(visibleText);
  }, [visibleText, onVisibleTextChange]);

  // -----------------------------------------------------------
  // #1: Listen for "streamData" from the socket
  // -----------------------------------------------------------
  useEffect(() => {
    if (!socket || !sessionId) return;
    debugLog("Attach 'streamData' => socket.id=", socket.id);

    const streamHandler = data => {
      debugLog("streamData =>", data);
      if (!firstStreamDataSeenRef.current) {
        firstStreamDataSeenRef.current = true;
        onFirstStreamData?.(data);
      }
      processData(data);
    };

    socket.on("streamData", streamHandler);

    return () => {
      debugLog("Detach 'streamData'");
      socket.off("streamData", streamHandler);
    };
  }, [socket, sessionId, onFirstStreamData]);

  // -----------------------------------------------------------
  // #2: Call /api/stream once
  // -----------------------------------------------------------
  useEffect(() => {
    if (!socket || !sessionId || !prompt) return;
    if (hasCalledApiRef.current) return;

    hasCalledApiRef.current = true;
    currentAudioRef.current?.pause();
    currentAudioRef.current = null;
    currentClipRef.current = null;
    setAudioQueue([]);
    setIsAudioPlaying(false);
    setFullText("");
    setVisibleText("");
    playbackEndedRef.current = false;
    playbackRunningRef.current = true;
    deliveryRef.current = null;
    receivedPacketsRef.current.clear();
    lastPacketRef.current = 0;
    lastAcknowledgementRef.current = null;
    lastCompletionRef.current = null;
    resumeRequestedRef.current = false;
    interruptionRef.current = "";
    lastProgressRef.current = Date.now();
    setFinalTTSReceived(false);
    debugLog("Calling /api/stream => prompt=", prompt);

    axios
      .get(
        `/api/stream?prompt=${encodeURIComponent(prompt)}&sessionId=${sessionId}&voice=${encodeURIComponent(
          voice
        )}&qualityMode=${encodeURIComponent(qualityMode || "regular")}`
      )
      .then(response => {
      if (!deliveryRef.current && !playbackEndedRef.current) deliveryRef.current = response.data.deliveryId || null;
      debugLog("Streaming initiated =>", response.data.message);
    })
    .catch(error => {
      console.error("Error initiating stream =>", error);
        onStreamInitError?.(error);
    });
    }, [socket, sessionId, prompt, voice, qualityMode, onStreamInitError]);

  // -----------------------------------------------------------
  // processPendingStopMusic => flush stopMusic lines after finalTTS
  // -----------------------------------------------------------
  const processPendingStopMusic = () => {
    if (pendingStopMusicRef.current.length > 0) {
      debugLogCustomMusic("Flushing stopMusic => maybe custom music...");
      pendingStopMusicRef.current.forEach(clip => {
        if (window.bgMusicManager?.addToCustomPlaylist) {
          debugLogCustomMusic("Add custom music...");
          window.bgMusicManager.addToCustomPlaylist(clip.audioUrl);
        }
      });
      onCustomMusicAvailable?.();
      pendingStopMusicRef.current = [];
    }
  };

  // -----------------------------------------------------------
  // #3: Main queue effect => picks next clip
  // -----------------------------------------------------------
  useEffect(() => {
    // If user manually paused, do NOT pick the next clip
    if (manuallyPaused) {
      debugLog("[QUEUE EFFECT] Manually paused => Skip picking next clip");
      return;
    }
  
    // If something is already playing (or a pause timer is active), do nothing
    if (isAudioPlaying || currentClipRef.current) {
      debugLogQueueWaiting("[QUEUE EFFECT] Currently isAudioPlaying => Do nothing");
      return;
    }
  
    // If queue is empty, do nothing
    if (audioQueue.length === 0) {
      debugLogQueueWaiting("[QUEUE EFFECT] audioQueue is empty => Nothing to process");
      return;
    }
  
    // Take the first item in the queue
    const clip = audioQueue[0];
    if (clip.finalTTS) {
      playbackHandlersRef.current.completePlayback(Boolean(interruptionRef.current), interruptionRef.current ? "stream_interrupted" : "complete");
      return;
    }
    currentClipRef.current = clip;
    lastProgressRef.current = Math.max(lastProgressRef.current, Date.now());
    setIsAudioPlaying(true);
    debugLog("[QUEUE EFFECT] Processing clip =>", clip);
  
    // Validate numeric pause
    let pauseMs = 0;
    if (Number.isFinite(clip.pause) && clip.pause > 0) {
      pauseMs = clip.pause * 1000;
      lastProgressRef.current = Date.now() + pauseMs;
      debugLog(`[QUEUE EFFECT] Detected pause => ${pauseMs} ms`);
    }
  
    const processClip = () => {
  
      // If the queue changed mid‐pause, bail
      if (currentClipRef.current !== clip) {
        debugLog("[QUEUE EFFECT] Clip changed mid‐pause => skip old clip");
        setIsAudioPlaying(false);
        return;
      }
  
      // If there's text, append it
      if (clip.writtenText && clip.writtenText.trim().length > 0) {
        let appendedText = clip.writtenText;
        // If priority, optionally mark it in some special way
        if (clip.priority) {
          appendedText = appendedText
            .split("\n")
            .map(line => (line.trim() ? `*${line.trim()}*` : ""))
            .join("\n");
        }
        setFullText(prev => mergeTextWithNewlines(prev, appendedText));
        setVisibleText(prev => mergeTextWithNewlines(prev, appendedText));
        debugLog("[QUEUE EFFECT] Appended text =>", appendedText);
      }
  
      // If there's an audioUrl, play it
      const audioSource = clip.audioUrl || (pauseMs > 0 ? createPauseAudioUrl(clip.pause) : null);
      if (audioSource && !clip.stopMusic) {
        debugLog("[QUEUE EFFECT] Playing audio");
        const audioObj = new Audio(audioSource);
        audioObj.volume = globalVolumeRef.current ?? 1.0;
        currentAudioRef.current = audioObj;
        audioPositionRef.current = 0;

        const acknowledge = () => {
          const acknowledgement = { sessionId, ...(clip.packetId !== undefined ? { deliveryId: clip.deliveryId, packetId: clip.packetId } : {}) };
          lastAcknowledgementRef.current = acknowledgement;
          socket.emit("finishedLine", acknowledgement);
        };
  
        let finishedNotified = false;
  
        audioObj.addEventListener("timeupdate", () => {
          if (currentAudioRef.current === audioObj && audioObj.currentTime > audioPositionRef.current) {
            audioPositionRef.current = audioObj.currentTime;
            lastProgressRef.current = Math.max(lastProgressRef.current, Date.now());
          }
          if (currentAudioRef.current !== audioObj || !clip.audioUrl) return;
          // If we haven't already notified and near the end => notify server
          if (
            !finishedNotified &&
            (audioObj.duration - audioObj.currentTime <= 2)
          ) {
            finishedNotified = true;
            if (!clip.priority && socket && sessionId) {
              debugLog("[QUEUE EFFECT] near end => emit('finishedLine')");
              acknowledge();
            }
          }
        });
  
        audioObj.addEventListener("ended", () => {
          if (currentAudioRef.current !== audioObj) return;
          if (!finishedNotified && socket && sessionId && !clip.priority && clip.audioUrl) {
            debugLog("[QUEUE EFFECT] ended => emit('finishedLine')");
            acknowledge();
          }
          // Remove the clip from queue and do a short gap
          setIsAudioPlaying(false);
          setAudioQueue(prev => {
            const newQueue = prev.slice(1); // remove first
            // Insert a 1s pause clip so we don't butt up the next audio
            var isAudioClip = clip.audioUrl && clip.audioUrl.length > 0;
            if (isAudioClip && !newQueue[0]?.finalTTS) {
              newQueue.unshift({
                pause: 1,
                text: "",
                writtenText: "",
                audioUrl: null,
                media: null,
                stopMusic: false,
                finalTTS: false,
                priority: clip.priority
              });
            }
            return newQueue;
          });
          currentClipRef.current = null;
          currentAudioRef.current = null;
          lastProgressRef.current = Math.max(lastProgressRef.current, Date.now());
        });

        const handleAudioError = error => {
          if (currentAudioRef.current !== audioObj) return;
          console.error("Meditation audio playback failed:", error);
          interruptionRef.current = "An audio clip could not be played. You can start the meditation again.";
          if (!finishedNotified && socket && sessionId && !clip.priority && clip.audioUrl) {
            finishedNotified = true;
            acknowledge();
          }
          audioObj.pause();
          currentAudioRef.current = null;
          currentClipRef.current = null;
          setAudioQueue(prev => {
            const newQueue = prev[0] === clip ? prev.slice(1) : prev;
            return newQueue;
          });
          setIsAudioPlaying(false);
        };
        audioObj.addEventListener("error", handleAudioError);
  
        audioObj
          .play()
          .then(() => {
            if (currentAudioRef.current !== audioObj || !clip.audioUrl) return;
            if (clip.media) {
              debugLog("[QUEUE EFFECT] sending media command =>", clip.media);
              onMediaCommand?.(clip.media);
            }
            onTTSPlay?.(clip.text);
            if (!clip.priority && clip.writtenText) {
              onNewNonPriorityText?.(clip.writtenText);
            }
          })
          .catch(handleAudioError);
      } else {
        // No audio => Could be pause-only, media-only, or finalTTS
        debugLog("[QUEUE EFFECT] No audio in clip =>", clip);
  
        // If there's a media command, call it
        if (clip.media) {
          debugLog("[QUEUE EFFECT] sending media command =>", clip.media);
          onMediaCommand?.(clip.media);
        }
  
        // Remove the clip from the queue
        setAudioQueue(prev => {
          const newQueue = prev.slice(1);
          return newQueue;
        });
        setIsAudioPlaying(false);
        currentClipRef.current = null;
        currentAudioRef.current = null;
      }
    };
    processClip();
  }, [
    audioQueue,
    finalTTSReceived,
    isAudioPlaying,
    manuallyPaused,
    onMediaCommand,
    onLastTTSComplete,
    onNewNonPriorityText,
    onTTSPlay,
    sessionId,
    socket
  ]);
  

  // -----------------------------------------------------------
  // #4: If no finalTTS, queue empty, not playing => poke server
  // -----------------------------------------------------------
  useEffect(() => {
    if (!finalTTSReceived && audioQueue.length === 0 && !isAudioPlaying && socket && sessionId) {
      const intervalId = setInterval(() => {
        if (
          !finalTTSReceived &&
          audioQueue.length === 0 &&
          !isAudioPlaying &&
          socket &&
          sessionId
        ) {
          if (!sentIdleLine) {
            socket.emit("finishedLine", { sessionId });
            setSentIdleLine(true);
          }
        }
      }, 2000);
      return () => clearInterval(intervalId);
    }
  }, [finalTTSReceived, audioQueue, isAudioPlaying, socket, sessionId, sentIdleLine]);

  // -----------------------------------------------------------
  // Public methods
  // -----------------------------------------------------------
  useImperativeHandle(ref, () => ({
    getDeliveryId: () => deliveryRef.current,
    synchronizeSessionState: snapshot => {
      if (!playbackRunningRef.current || playbackEndedRef.current || !deliveryRef.current) return;
      if (snapshot.deliveryId && deliveryRef.current && snapshot.deliveryId !== deliveryRef.current) return;
      if (snapshot.phase === "completed") completePlayback();
      if (snapshot.phase === "interrupted" || snapshot.phase === "idle") {
        interruptionRef.current = "The connection could not restore this meditation. You can start again.";
        completePlayback(true, "session_state_interrupted");
      }
    },
    stopCustomMusicLoop: () => {
      pendingStopMusicRef.current = [];
      onCustomMusicStop?.();
    },
    restartCustomMusicLoop: () => {
      debugLog("restartCustomMusicLoop => not implemented");
    },
    insertPriorityAudio(priorityData) {
      // We simply pass 'true' for isPriority to processData
      processData(priorityData, true);
    },
    updateGlobalVolume: volume => {
      if (currentAudioRef.current) {
        currentAudioRef.current.volume = volume;
      }
    },
    pausePlayback: () => {
      setManuallyPaused(true);

      currentAudioRef.current?.pause();
    },
    resumePlayback: () => {
      setManuallyPaused(false);

      const clip = currentClipRef.current;
      if (!clip) return; // nothing to resume

      // If the clip was removed from the queue (like a timed pause), no resume
      if (!audioQueue.includes(clip)) {
        debugLog("resumePlayback => clip not in queue, skipping");
        return;
      }

      // If mid-audio, rewind slightly and resume
      if (currentAudioRef.current) {
        const audioObj = currentAudioRef.current;
        if (clip.audioUrl) {
          audioObj.currentTime = Math.max(0, audioObj.currentTime - 2);
        }
        audioObj.play().catch(err => console.error("Resume error:", err));
      }
    },
    resetForReplay: () => {
      debugLog("Resetting StreamingMeditation for replay...");
      firstStreamDataSeenRef.current = false;
      playbackEndedRef.current = false;
      playbackRunningRef.current = true;
      deliveryRef.current = null;
      receivedPacketsRef.current.clear();
      lastPacketRef.current = 0;
      lastAcknowledgementRef.current = null;
      lastCompletionRef.current = null;
      resumeRequestedRef.current = false;
      interruptionRef.current = "";
      lastProgressRef.current = Date.now();
      setFullText("");
      setVisibleText("");
      setAudioQueue([]);
      setFinalTTSReceived(false);
      setIsAudioPlaying(false);
      setManuallyPaused(false);

      // Stop/clear any playing audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      currentClipRef.current = null;
    },
    preventStreamApiOnce: () => {
      hasCalledApiRef.current = true;
    },
    allowStreamApi: () => {
      firstStreamDataSeenRef.current = false;
      hasCalledApiRef.current = false;
    }
  }));

  return (
    <div>
      <div
        className="meditation-text"
        style={{ fontSize: visibleText === "" ? "0.95rem" : "1rem" }}
      >
        <ReactMarkdown>
          {visibleText === ""
            ? (
              isExistingSession 
                ? `Before generating a meditation, take a moment to reflect on what truly matters to you right now. Feel free to include any emotions, intentions, or challenges you’d like to explore.

The more details you share, the more personal and supportive your meditation will be.

Type these details into the text box below, choose a Voice, then press Generate Meditation.

You can double-click the video to toggle its size.

The meditation responds to Relaxation changes, which include Resonance and average heart rate.

Replay meditations using the circular history button.`
                :`Before generating a meditation, take a moment to reflect on what truly matters to you right now.

Feel free to include any emotions, intentions, or challenges you’d like to explore.

The more details you share, the more personal and supportive your meditation will be.

Type these details into the text box below, choose a Voice, then press Generate Meditation.

You can double-click the video to toggle its size.

Replay meditations using the circular history button.`
  )
            : visibleText}
        </ReactMarkdown>
      </div>
    </div>
  );
});

export default StreamingMeditation;
